import { RefObject } from "react";

import { createIsolationFullAction } from "@/data/isolation/actions/createIsolationFullAction";
import { AudioInputState, UserNoDates } from "@/types/types";
import { isSelectedCover } from "@/utils/selectedFileCover/is/isSelectedCover";
import { isSelectedFile } from "@/utils/selectedFileCover/is/isSelectedFile";
import { stringifyDatesSelectedFileCover } from "@/utils/serverClientSerialization/stringifyDatesSelectedFileCover";
import { isValidLengthSubscription } from "@/utils/subscriptionLevels/time/isValidLengthSubscription";

export const handleSubmit = async ({
  isolationId,
  selectedFileCover,
  clearInput,
  user,

  removeInstrumental,
  removeBackgroundVocals,
  deReverbDeEcho,
  deNoise,

  selectedFileCoverRef,
}: {
  selectedFileCover: Exclude<AudioInputState["selectedFileCover"], null>;
  isolationId: string;
  clearInput: () => void;
  user: UserNoDates;

  removeInstrumental: boolean;
  removeBackgroundVocals: boolean;
  deReverbDeEcho: boolean;
  deNoise: boolean;

  selectedFileCoverRef: RefObject<AudioInputState["selectedFileCover"]>;
}) => {
  // Wait for the selectedFileCover to finish uploading
  const keepGoing = await new Promise<boolean>((resolve) => {
    // Immediately resolve if progress is already null
    if (
      selectedFileCover === null ||
      isSelectedCover(selectedFileCover) ||
      selectedFileCover.progress === null
    ) {
      resolve(true);
    } else {
      // Setup a periodic check for the progress to become null
      const intervalId = setInterval(() => {
        // Check if the selectedFileCover has changed to null or Cover
        if (
          selectedFileCoverRef.current === null ||
          isSelectedCover(selectedFileCoverRef.current)
        ) {
          clearInterval(intervalId);
          resolve(false);
        } else {
          // If still a File, check if it is another file
          if (
            selectedFileCoverRef.current.selectedUrl !==
            selectedFileCover.selectedUrl
          ) {
            clearInterval(intervalId);
            resolve(false);
          }

          // If progress is null = upload is done, resolve
          if (selectedFileCoverRef.current.progress === null) {
            clearInterval(intervalId);
            resolve(true);
          }
        }
      }, 400); // Check every 400 ms, adjust as necessary
    }
  });

  if (!keepGoing || selectedFileCoverRef.current === null) {
    return;
  }

  const selectedFileCoverRefCurrent = selectedFileCoverRef.current;

  const selectedFileCoverIsFile = isSelectedFile(selectedFileCoverRefCurrent);

  let lengthS: number;

  // Is File
  if (selectedFileCoverIsFile) {
    if (
      selectedFileCoverRefCurrent.selectedSound === null ||
      selectedFileCoverRefCurrent.progress !== null
    ) {
      throw new Error("No sound added");
    }

    lengthS = selectedFileCoverRefCurrent.selectedSound.lengthS;

    // Is Cover
  } else {
    lengthS = selectedFileCoverRefCurrent.audioLength;
  }

  await createIsolationFullAction({
    isolationId: isolationId,
    removeInstrumental: removeInstrumental,
    removeBackgroundVocals: removeBackgroundVocals,
    deReverbDeEcho: deReverbDeEcho,
    deNoise: deNoise,

    selectedFileCover: stringifyDatesSelectedFileCover(
      selectedFileCoverRefCurrent
    ),
  });

  // Check if the user has enough time left in their subscription
  if (user.conversionTimeLeftSeconds !== null) {
    const validAudioLength = isValidLengthSubscription(
      user.conversionTimeLeftSeconds - lengthS,
      lengthS
    );

    if (!validAudioLength) {
      clearInput();
    }
  }
};
